import React from 'react';
import styled from 'styled-components';

interface CardProps {
  children: React.ReactNode;
  padding?: string;
  className?: string;
}

interface CardHeaderProps {
  title: React.ReactNode;
  subtitle?: string;
  meta?: React.ReactNode;
  children?: React.ReactNode;
  titleVariant?: 'default' | 'section';
}

export const Card: React.FC<CardProps> = ({ children, padding, className }) => (
  <CardWrapper $padding={padding} className={className}>
    {children}
  </CardWrapper>
);

export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  meta,
  children,
  titleVariant = 'default'
}) => (
  <HeaderWrapper>
    <HeaderLeft>
      <CardTitle $variant={titleVariant}>{title}</CardTitle>
      {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}
    </HeaderLeft>
    {(meta || children) && <HeaderRight>{meta || children}</HeaderRight>}
  </HeaderWrapper>
);

const CardWrapper = styled.div<{ $padding?: string }>`
  position: relative;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: ${({ $padding }) => $padding || '0'};
  overflow: hidden;
`;

const HeaderWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px;
  flex-wrap: wrap;
`;

const HeaderLeft = styled.div`
  min-width: 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const CardTitle = styled.h3<{ $variant: 'default' | 'section' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${({ $variant }) =>
    $variant === 'section' ? 'var(--font-sans)' : 'var(--font-serif)'};
  font-size: 17px;
  font-weight: ${({ $variant }) => ($variant === 'section' ? 600 : 500)};
  color: var(--ink);
  margin: 0 0 2px;
  letter-spacing: -0.01em;
  ${({ $variant }) =>
    $variant === 'section' &&
    `
      letter-spacing: 0.03em;
      text-transform: uppercase;
    `}
`;

const CardSubtitle = styled.p`
  font-size: 12.5px;
  color: var(--ink-muted);
  margin: 0;
  line-height: 1.4;
`;
